import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProjectIntroduction = () => {
  const projectHighlights = [
  {
    icon: 'Users',
    title: 'Cross-Border Cooperation',
    description: 'Strengthening ties between Serbia and Montenegro through shared heritage promotion'
  },
  {
    icon: 'Globe',
    title: 'Cultural Exchange',
    description: 'Facilitating cultural understanding and tourism development across the region'
  },
  {
    icon: 'Leaf',
    title: 'Sustainable Tourism',
    description: 'Promoting responsible travel that preserves natural and cultural heritage'
  },
  {
    icon: 'Heart',
    title: 'Community Impact',
    description: 'Supporting local communities through heritage-based tourism initiatives'
  }];


  const partnerLogos = [
  {
    name: 'European Union',
    logo: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763512232/Flag_of_Europe.svg_ku7fyb.png",
    logoAlt: 'European Union flag with blue background and circle of golden stars'
  },
  {
    name: 'Interreg IPA',
    logo: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763512227/CBC-SRB-MNE-logo-2014_2020-beli_rhls3s.png",
    logoAlt: 'Interreg IPA cross-border cooperation program official logo'
  },
  {
    name: 'Serbia Tourism',
    logo: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763512232/Coat_of_arms_of_Serbia_small.svg_f7vhbg.png",
    logoAlt: 'Serbia Tourism Organization official logo with national symbols'
  },
  {
    name: 'Montenegro Tourism',
    logo: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763512231/Coat_of_arms_of_Montenegro.svg_s0bops.png",
    logoAlt: 'Montenegro National Tourism Organization official logo'
  }];


  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Project Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Icon name="Award" size={16} />
                EU Funded Project
              </div>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-4">
                Creative Twinning Project
              </h2>
              <h3 className="font-heading font-semibold text-xl text-muted-foreground mb-6">
                Kreativna Saradnja
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                An innovative cross-border initiative connecting Serbia and Montenegro through shared cultural heritage, 
                natural beauty, and sustainable tourism development. Our mission is to create lasting partnerships that 
                celebrate the rich history and traditions of the Balkan region while fostering economic growth through 
                responsible tourism.
              </p>
            </div>

            {/* Project Highlights */}
            <div className="grid sm:grid-cols-2 gap-4">
              {projectHighlights?.map((highlight, index) =>
              <div key={index} className="flex gap-3 p-4 bg-surface rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={highlight?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-1">
                      {highlight?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {highlight?.description}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/about-project">
                <Button variant="default" size="lg" iconName="Info" iconPosition="left">
                  Learn More
                </Button>
              </Link>
              <Link to="/contact-and-partners">
                <Button variant="outline" size="lg" iconName="Users" iconPosition="left">
                  Our Partners
                </Button>
              </Link>
            </div>
          </div>

          {/* Visual Content */}
          <div className="space-y-8">
            {/* Main Project Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <Image
                src="https://images.unsplash.com/photo-1618694362249-6b1466e243ce"
                alt="Aerial view of traditional Balkan village with red-tiled roofs nestled between green mountains and clear blue river"
                className="w-full h-64 object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-sm font-medium">Cross-Border Heritage</p>
                <p className="text-xs opacity-90">Connecting Communities</p>
              </div>
            </div>

            {/* Partner Logos */}
            <div className="bg-surface rounded-xl p-6">
              <h4 className="font-heading font-semibold text-center text-foreground mb-6">
                Supported By
              </h4>
              <div className="grid grid-cols-2 gap-6">
                {partnerLogos?.map((partner, index) =>
                <div key={index} className="flex items-center justify-center p-4 bg-background rounded-lg">
                    <Image
                    src={partner?.logo}
                    alt={partner?.logoAlt}
                    className="h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />

                  </div>
                )}
              </div>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-primary/5 rounded-lg">
                <div className="font-bold text-2xl text-primary">2</div>
                <div className="text-xs text-muted-foreground">Countries</div>
              </div>
              <div className="text-center p-4 bg-accent/5 rounded-lg">
                <div className="font-bold text-2xl text-accent">8</div>
                <div className="text-xs text-muted-foreground">Trails</div>
              </div>
              <div className="text-center p-4 bg-success/5 rounded-lg">
                <div className="font-bold text-2xl text-success">77+</div>
                <div className="text-xs text-muted-foreground">Sites</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ProjectIntroduction;